import React, { useState, useEffect, useRef } from 'react';
import {
    MessageCircle,
    Send,
    X,
    Users,
    Trash2,
    ChevronDown,
    ArrowLeft,
    Bell
} from 'lucide-react';
import { API_CONFIG } from './config/apiConfig';

// localStorage keys
const privLastReadKey = (myId, partnerId) =>
    `csp_lr_priv_${Math.min(myId, partnerId)}_${Math.max(myId, partnerId)}`;
const pubLastReadKey = (myId) => `csp_lr_pub_${myId}`;

// 🔊 Son normal (2 bips)
const playNotificationSound = () => {
    try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const bip = (freq, t) => {
            const o = ctx.createOscillator(), g = ctx.createGain();
            o.connect(g); g.connect(ctx.destination);
            o.frequency.value = freq; o.type = 'sine';
            g.gain.setValueAtTime(0.3, t);
            g.gain.exponentialRampToValueAtTime(0.001, t + 0.12);
            o.start(t); o.stop(t + 0.12);
        };
        bip(800, ctx.currentTime);
        bip(1000, ctx.currentTime + 0.15);
    } catch (e) {}
};

// 🔕 Son discret (film en cours)
const playNotificationSoundSoft = () => {
    try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const o = ctx.createOscillator(), g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.frequency.value = 880; o.type = 'sine';
        g.gain.setValueAtTime(0.07, ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
        o.start(ctx.currentTime); o.stop(ctx.currentTime + 0.35);
    } catch (e) {}
};

const ChatWidget = ({ currentUser, isWatchingVideo }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [allMembers, setAllMembers] = useState([]);
    const [onlineCount, setOnlineCount] = useState(0);
    const [sending, setSending] = useState(false);
    const [activeTab, setActiveTab] = useState('public');
    const [selectedUser, setSelectedUser] = useState(null);
    const [privateMessages, setPrivateMessages] = useState([]);
    const [isUserScrolling, setIsUserScrolling] = useState(false);
    const [newMessagesCount, setNewMessagesCount] = useState(0);
    const isUserScrollingRef = useRef(false);
    const [toasts, setToasts] = useState([]);

    // Unread counts
    const [privateUnread, setPrivateUnread] = useState({}); // { senderId: count }
    const [publicUnread, setPublicUnread] = useState(0);

    const totalUnread = publicUnread + Object.values(privateUnread).reduce((a, b) => a + b, 0);

    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const lastScrollTop = useRef(0);
    const lastMessageIdRef = useRef(0);
    const lastPrivateCheckRef = useRef(0);
    const initDoneRef = useRef(false);
    const isOpenRef = useRef(false);
    const activeTabRef = useRef('public');
    const selectedUserRef = useRef(null);
    const isWatchingVideoRef = useRef(false);

    useEffect(() => { isOpenRef.current = isOpen; }, [isOpen]);
    useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);
    useEffect(() => { selectedUserRef.current = selectedUser; }, [selectedUser]);
    useEffect(() => { isWatchingVideoRef.current = isWatchingVideo; }, [isWatchingVideo]);
    useEffect(() => { isUserScrollingRef.current = isUserScrolling; }, [isUserScrolling]);

    const addToast = (text, fromUser) => {
        const id = Date.now() + Math.random();
        setToasts(prev => [...prev.slice(-2), { id, text, fromUser }]); // max 3 toasts
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 5000);
    };

    const getHeaders = () => ({
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Admin-Token': localStorage.getItem('cinestream_token') || ''
    });

    // ─── Init : calcul des non-lus au chargement (fonctionne même après reconnexion) ───
    useEffect(() => {
        if (!currentUser) return;

        const init = async () => {
            try {
                // Messages privés
                const rPriv = await fetch(
                    `${API_CONFIG.NEW_API_BASE}/chat/messages?private=1&user_id=${currentUser.id}`,
                    { headers: getHeaders() }
                );
                const dPriv = await rPriv.json();
                if (dPriv.success && dPriv.messages?.length > 0) {
                    lastPrivateCheckRef.current = Math.max(...dPriv.messages.map(m => m.id));

                    const counts = {};
                    dPriv.messages.forEach(msg => {
                        if (msg.user_id === currentUser.id) return; // envoyé par moi
                        const lastRead = parseInt(
                            localStorage.getItem(privLastReadKey(currentUser.id, msg.user_id)) || '0'
                        );
                        if (msg.id > lastRead) {
                            counts[msg.user_id] = (counts[msg.user_id] || 0) + 1;
                        }
                    });
                    setPrivateUnread(counts);
                }
            } catch (e) {}

            try {
                // Messages publics
                const rPub = await fetch(`${API_CONFIG.NEW_API_BASE}/chat/messages`, { headers: getHeaders() });
                const dPub = await rPub.json();
                if (dPub.success && dPub.messages?.length > 0) {
                    const latestId = Math.max(...dPub.messages.map(m => m.id));
                    lastMessageIdRef.current = latestId;
                    const lastRead = parseInt(localStorage.getItem(pubLastReadKey(currentUser.id)) || '0');
                    setPublicUnread(dPub.messages.filter(m => m.id > lastRead).length);
                }
            } catch (e) {}

            initDoneRef.current = true;
        };

        init();
    }, [currentUser]);

    // ─── Marquer les messages privés comme lus quand la conversation est ouverte ───
    useEffect(() => {
        if (!selectedUser || privateMessages.length === 0) return;
        const latestId = Math.max(...privateMessages.map(m => m.id));
        localStorage.setItem(privLastReadKey(currentUser.id, selectedUser.id), latestId.toString());
        setPrivateUnread(prev => {
            if (!prev[selectedUser.id]) return prev;
            const next = { ...prev };
            delete next[selectedUser.id];
            return next;
        });
    }, [selectedUser, privateMessages]);

    // ─── Marquer les messages publics comme lus quand on regarde l'onglet public ───
    useEffect(() => {
        if (!isOpen || activeTab !== 'public' || messages.length === 0) return;
        const latestId = Math.max(...messages.map(m => m.id));
        localStorage.setItem(pubLastReadKey(currentUser.id), latestId.toString());
        setPublicUnread(0);
    }, [isOpen, activeTab, messages]);

    // ─── Background polling : messages privés ───
    useEffect(() => {
        if (!currentUser) return;

        const check = async () => {
            if (!initDoneRef.current) return;
            try {
                const r = await fetch(
                    `${API_CONFIG.NEW_API_BASE}/chat/messages?private=1&user_id=${currentUser.id}`,
                    { headers: getHeaders() }
                );
                const d = await r.json();
                if (!d.success || !d.messages?.length) return;

                const newMsgs = d.messages.filter(
                    m => m.id > lastPrivateCheckRef.current && m.user_id !== currentUser.id
                );
                if (newMsgs.length === 0) return;

                lastPrivateCheckRef.current = Math.max(...d.messages.map(m => m.id));

                // Son (une seule fois pour le batch)
                isWatchingVideoRef.current ? playNotificationSoundSoft() : playNotificationSound();

                newMsgs.forEach(msg => {
                    const convOpen =
                        isOpenRef.current &&
                        activeTabRef.current === 'private' &&
                        selectedUserRef.current?.id === msg.user_id;

                    if (!convOpen) {
                        setPrivateUnread(prev => ({
                            ...prev,
                            [msg.user_id]: (prev[msg.user_id] || 0) + 1
                        }));
                        addToast(msg.message, msg.username);
                    }
                });
            } catch (e) {}
        };

        const interval = setInterval(check, 3000);
        return () => clearInterval(interval);
    }, [currentUser]);

    // ─── Background polling : messages publics (badge) ───
    useEffect(() => {
        if (!currentUser) return;

        const check = async () => {
            if (!initDoneRef.current) return;
            if (isOpenRef.current && activeTabRef.current === 'public') return;
            try {
                const r = await fetch(`${API_CONFIG.NEW_API_BASE}/chat/messages`, { headers: getHeaders() });
                const d = await r.json();
                if (!d.success || !d.messages?.length) return;
                const newMsgs = d.messages.filter(m => m.id > lastMessageIdRef.current);
                if (newMsgs.length > 0) {
                    lastMessageIdRef.current = Math.max(...d.messages.map(m => m.id));
                    setPublicUnread(prev => prev + newMsgs.length);
                }
            } catch (e) {}
        };

        const interval = setInterval(check, 5000);
        return () => clearInterval(interval);
    }, [currentUser]);

    // ─── Polling messages publics (chat ouvert) ───
    useEffect(() => {
        if (isOpen && activeTab === 'public') {
            fetchMessages();
            const interval = setInterval(fetchMessages, 2000);
            return () => clearInterval(interval);
        }
    }, [isOpen, activeTab]);

    // ─── Polling messages privés (conversation ouverte) ───
    useEffect(() => {
        if (selectedUser) {
            fetchPrivateMessages(selectedUser.id);
            const interval = setInterval(() => fetchPrivateMessages(selectedUser.id), 2000);
            return () => clearInterval(interval);
        }
    }, [selectedUser]);

    // ─── Polling membres ───
    useEffect(() => {
        if (!currentUser) return;
        fetchAllMembers();
        const interval = setInterval(fetchAllMembers, 10000);
        return () => clearInterval(interval);
    }, [currentUser]);

    const handleScroll = () => {
        const c = messagesContainerRef.current;
        if (!c) return;
        const { scrollTop, scrollHeight, clientHeight } = c;
        const atBottom = scrollHeight - scrollTop - clientHeight < 50;
        if (atBottom) { setIsUserScrolling(false); setNewMessagesCount(0); }
        else if (scrollTop < lastScrollTop.current) setIsUserScrolling(true);
        lastScrollTop.current = scrollTop;
    };

    const fetchMessages = async () => {
        try {
            const r = await fetch(`${API_CONFIG.NEW_API_BASE}/chat/messages`, { headers: getHeaders() });
            const d = await r.json();
            if (d.success) {
                const msgs = d.messages || [];
                if (msgs.length > 0) {
                    const latestId = Math.max(...msgs.map(m => m.id));
                    if (lastMessageIdRef.current > 0 && latestId > lastMessageIdRef.current && isUserScrollingRef.current) {
                        setNewMessagesCount(prev => prev + 1);
                    }
                    lastMessageIdRef.current = latestId;
                }
                setMessages(msgs);
                if (!isUserScrollingRef.current) setTimeout(() => scrollToBottom(true), 100);
            }
        } catch (e) {}
    };

    const fetchPrivateMessages = async (targetId) => {
        try {
            const r = await fetch(
                `${API_CONFIG.NEW_API_BASE}/chat/messages?private=1&user_id=${currentUser.id}&target_id=${targetId}`,
                { headers: getHeaders() }
            );
            const d = await r.json();
            if (d.success) {
                setPrivateMessages(d.messages || []);
                if (!isUserScrollingRef.current) setTimeout(() => scrollToBottom(true), 100);
            }
        } catch (e) {}
    };

    const fetchAllMembers = async () => {
        try {
            const r = await fetch(`${API_CONFIG.NEW_API_BASE}/chat/members`, { headers: getHeaders() });
            const d = await r.json();
            if (d.success) {
                const members = (d.members || []).filter(u => u.id !== currentUser.id);
                setAllMembers(members);
                setOnlineCount((d.members || []).filter(m => m.status !== 'offline').length);
            }
        } catch (e) {}
    };

    const sendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || sending || !currentUser) return;
        setSending(true);
        try {
            const r = await fetch(`${API_CONFIG.NEW_API_BASE}/chat/messages`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({
                    user_id: currentUser.id,
                    username: currentUser.username,
                    message: newMessage.trim(),
                    is_private: activeTab === 'private' && selectedUser ? 1 : 0,
                    recipient_id: selectedUser?.id || null
                })
            });
            const d = await r.json();
            if (d.success) {
                setNewMessage('');
                activeTab === 'public' ? fetchMessages() : fetchPrivateMessages(selectedUser.id);
            }
        } catch (e) {}
        setSending(false);
    };

    const deleteMessage = async (messageId) => {
        if (!window.confirm('Supprimer ce message ?')) return;
        try {
            const r = await fetch(`${API_CONFIG.NEW_API_BASE}/chat/messages/${messageId}`, {
                method: 'DELETE',
                headers: getHeaders()
            });
            const d = await r.json();
            if (d.success) {
                setMessages(prev => prev.filter(m => m.id !== messageId));
                setPrivateMessages(prev => prev.filter(m => m.id !== messageId));
            }
        } catch (e) {}
    };

    const scrollToBottom = (force = false) => {
        if (!isUserScrolling || force) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleScrollToBottom = () => {
        setIsUserScrolling(false); setNewMessagesCount(0); scrollToBottom(true);
    };

    const formatTime = (ts) => new Date(ts).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

    const formatDateSep = (ts) => {
        const d = new Date(ts), today = new Date(), yest = new Date(today);
        yest.setDate(yest.getDate() - 1);
        if (d.toDateString() === today.toDateString()) return "Aujourd'hui";
        if (d.toDateString() === yest.toDateString()) return 'Hier';
        return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
    };

    const isSameDay = (ts1, ts2) => new Date(ts1).toDateString() === new Date(ts2).toDateString();

    const getStatusDot = (s) => s === 'watching' ? 'bg-red-500' : s === 'online' ? 'bg-green-500' : 'bg-gray-500';
    const getStatusLabel = (user) => {
        if (user.status === 'watching') return `🎬 ${user.watching || 'En visionnage'}`;
        if (user.status === 'online') return 'En ligne';
        return 'Hors ligne';
    };

    // Membres triés : ceux avec non-lus en premier, puis online, watching, offline
    const sortedMembers = [...allMembers].sort((a, b) => {
        const ua = privateUnread[a.id] || 0, ub = privateUnread[b.id] || 0;
        if (ub !== ua) return ub - ua; // plus de non-lus en premier
        const order = { online: 0, watching: 1, offline: 2 };
        return (order[a.status] ?? 3) - (order[b.status] ?? 3);
    });

    const displayMessages = activeTab === 'public' ? messages : privateMessages;

    const statusColor = (s) => s === 'watching' ? '#EF4444' : s === 'online' ? '#22C55E' : 'var(--text-4)';

    // ─── Toasts ───
    const ToastList = () => (
        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 60, display: 'flex', flexDirection: 'column', gap: 8, pointerEvents: 'none' }}>
            {toasts.map(t => (
                <div
                    key={t.id}
                    style={{
                        background: 'var(--bg-modal)',
                        border: '1px solid var(--glass-border)',
                        borderRadius: 'var(--r-xl)',
                        boxShadow: 'var(--shadow-lg)',
                        padding: 12,
                        width: 288,
                        backdropFilter: 'blur(16px)',
                        animation: 'fadeUp 0.3s ease-out',
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                        <Bell style={{ width: 14, height: 14, color: 'var(--accent)', flexShrink: 0, marginTop: 2 }} />
                        <div style={{
                            width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                            background: 'linear-gradient(135deg, #3B82F6, #06B6D4)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#fff', fontSize: '0.75rem', fontWeight: 700,
                        }}>
                            {(t.fromUser || '?').slice(0, 1).toUpperCase()}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{ color: 'var(--text-1)', fontSize: '0.75rem', fontWeight: 700, marginBottom: 2 }}>{t.fromUser}</p>
                            <p style={{ color: 'var(--text-3)', fontSize: '0.75rem', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{t.text}</p>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );

    const iconBtnStyle = {
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 32, height: 32, borderRadius: 'var(--r-md)',
        background: 'transparent', border: 'none',
        color: 'rgba(255,255,255,0.7)', cursor: 'pointer',
        transition: 'background 150ms ease, color 150ms ease',
        flexShrink: 0,
    };

    return (
        <>
            <ToastList />

            {!isOpen ? (
                <button
                    onClick={() => setIsOpen(true)}
                    style={{
                        position: 'fixed',
                        bottom: 'calc(var(--mobile-nav-h) + 12px)',
                        right: 20,
                        zIndex: 50,
                        width: 52, height: 52, borderRadius: '50%',
                        background: 'linear-gradient(135deg, var(--accent), var(--accent-deep))',
                        border: 'none', cursor: 'pointer', color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 8px 24px var(--accent-glow)',
                        transition: 'transform 200ms ease, box-shadow 200ms ease',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.boxShadow = '0 12px 32px var(--accent-glow)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 8px 24px var(--accent-glow)'; }}
                >
                    <MessageCircle style={{ width: 22, height: 22 }} />
                    {totalUnread > 0 && (
                        <span style={{
                            position: 'absolute', top: -4, right: -4,
                            background: '#EF4444', color: '#fff',
                            fontSize: '0.625rem', fontWeight: 700,
                            borderRadius: '99px', minWidth: 18, height: 18,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            padding: '0 4px',
                            border: '2px solid var(--bg-base)',
                            animation: 'glowPulse 2s infinite',
                        }}>
                            {totalUnread > 99 ? '99+' : totalUnread}
                        </span>
                    )}
                </button>
            ) : (
                <div style={{
                    position: 'fixed',
                    bottom: 'calc(var(--mobile-nav-h) + 12px)',
                    right: 20,
                    zIndex: 50,
                    width: 384,
                    maxWidth: 'calc(100vw - 32px)',
                    height: 600,
                    maxHeight: 'calc(100vh - 120px)',
                    background: 'var(--bg-modal)',
                    borderRadius: 'var(--r-2xl)',
                    boxShadow: 'var(--shadow-xl)',
                    border: '1px solid var(--glass-border)',
                    display: 'flex', flexDirection: 'column',
                    overflow: 'hidden',
                    animation: 'fadeUp 0.2s ease-out',
                }}>
                    {/* Header */}
                    <div style={{
                        background: 'linear-gradient(135deg, var(--accent), var(--accent-deep))',
                        padding: '14px 16px',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        flexShrink: 0,
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            {selectedUser && activeTab === 'private' ? (
                                <>
                                    <button
                                        onClick={() => { setSelectedUser(null); setActiveTab('public'); }}
                                        style={iconBtnStyle}
                                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
                                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                    >
                                        <ArrowLeft style={{ width: 18, height: 18 }} />
                                    </button>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <span style={{ width: 10, height: 10, borderRadius: '50%', background: statusColor(selectedUser.status), display: 'inline-block', flexShrink: 0 }} />
                                            <div>
                                                <p style={{ color: '#fff', fontWeight: 700, fontSize: '0.9375rem', lineHeight: 1.2 }}>{selectedUser.username}</p>
                                                <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.6875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>{getStatusLabel(selectedUser)}</p>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div style={{
                                        width: 36, height: 36, borderRadius: '50%',
                                        background: 'rgba(255,255,255,0.15)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <MessageCircle style={{ width: 18, height: 18, color: '#fff' }} />
                                    </div>
                                    <div>
                                        <p style={{ color: '#fff', fontWeight: 700, fontSize: '0.9375rem', lineHeight: 1.2 }}>Chat en ligne</p>
                                        <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.6875rem' }}>
                                            {onlineCount} {onlineCount > 1 ? 'utilisateurs' : 'utilisateur'} en ligne
                                        </p>
                                    </div>
                                </>
                            )}
                        </div>
                        <button
                            onClick={() => setIsOpen(false)}
                            style={iconBtnStyle}
                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                            <X style={{ width: 18, height: 18 }} />
                        </button>
                    </div>

                    {/* Tabs */}
                    {!selectedUser && (
                        <div style={{ display: 'flex', background: 'var(--glass)', borderBottom: '1px solid var(--glass-border)', flexShrink: 0 }}>
                            {[
                                { key: 'public', icon: <MessageCircle style={{ width: 14, height: 14 }} />, label: 'Public', badge: publicUnread },
                                {
                                    key: 'private',
                                    icon: <Users style={{ width: 14, height: 14 }} />,
                                    label: `Membres (${allMembers.length})`,
                                    badge: Object.values(privateUnread).reduce((a, b) => a + b, 0)
                                },
                            ].map(tab => (
                                <button
                                    key={tab.key}
                                    onClick={() => setActiveTab(tab.key)}
                                    style={{
                                        flex: 1, padding: '11px 16px',
                                        background: 'transparent', border: 'none',
                                        borderBottom: activeTab === tab.key ? '2px solid var(--accent)' : '2px solid transparent',
                                        color: activeTab === tab.key ? 'var(--accent-bright)' : 'var(--text-4)',
                                        cursor: 'pointer',
                                        fontSize: '0.8125rem', fontWeight: 600,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                                        position: 'relative',
                                        transition: 'color 150ms ease, border-color 150ms ease',
                                    }}
                                >
                                    {tab.icon}
                                    {tab.label}
                                    {tab.badge > 0 && (
                                        <span style={{
                                            background: '#EF4444', color: '#fff',
                                            fontSize: '0.5625rem', fontWeight: 700,
                                            borderRadius: '99px', minWidth: 16, height: 16,
                                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                            padding: '0 3px',
                                        }}>
                                            {tab.badge > 99 ? '99+' : tab.badge}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Content */}
                    <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                        {activeTab === 'private' && !selectedUser ? (
                            /* Members list */
                            <div style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {allMembers.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '32px 0' }}>
                                        <Users style={{ width: 40, height: 40, color: 'var(--text-4)', margin: '0 auto 8px', opacity: 0.5 }} />
                                        <p style={{ color: 'var(--text-4)', fontSize: '0.875rem' }}>Aucun membre</p>
                                    </div>
                                ) : (
                                    <>
                                        <div style={{ display: 'flex', gap: 12, color: 'var(--text-4)', fontSize: '0.6875rem', padding: '0 4px 8px', borderBottom: '1px solid var(--glass-border)', flexWrap: 'wrap' }}>
                                            {[['#22C55E', 'En ligne'], ['#EF4444', 'Film en cours'], ['var(--text-4)', 'Hors ligne']].map(([color, label]) => (
                                                <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, display: 'inline-block' }} />
                                                    {label}
                                                </span>
                                            ))}
                                        </div>
                                        {sortedMembers.filter(u => u.username).map((user) => {
                                            const unread = privateUnread[user.id] || 0;
                                            return (
                                                <button
                                                    key={user.id}
                                                    onClick={() => { setSelectedUser(user); setActiveTab('private'); }}
                                                    style={{
                                                        width: '100%',
                                                        display: 'flex', alignItems: 'center', gap: 10,
                                                        padding: '10px 12px',
                                                        borderRadius: 'var(--r-md)',
                                                        background: unread > 0
                                                            ? 'rgba(232,183,90,0.12)'
                                                            : 'var(--glass)',
                                                        border: `1px solid ${unread > 0 ? 'rgba(232,183,90,0.3)' : 'var(--glass-border)'}`,
                                                        cursor: 'pointer',
                                                        opacity: user.status === 'offline' && !unread ? 0.65 : 1,
                                                        transition: 'background 150ms ease, border-color 150ms ease',
                                                        textAlign: 'left',
                                                    }}
                                                    onMouseEnter={e => e.currentTarget.style.background = unread > 0 ? 'rgba(232,183,90,0.2)' : 'var(--glass-hover)'}
                                                    onMouseLeave={e => e.currentTarget.style.background = unread > 0 ? 'rgba(232,183,90,0.12)' : 'var(--glass)'}
                                                >
                                                    <div style={{ position: 'relative', flexShrink: 0 }}>
                                                        <div style={{
                                                            width: 36, height: 36, borderRadius: '50%',
                                                            background: 'linear-gradient(135deg, var(--accent), var(--accent-deep))',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            color: '#fff', fontWeight: 700, fontSize: '0.875rem',
                                                        }}>
                                                            {(user.username || '?').slice(0, 1).toUpperCase()}
                                                        </div>
                                                        <span style={{
                                                            position: 'absolute', bottom: -1, right: -1,
                                                            width: 11, height: 11, borderRadius: '50%',
                                                            background: statusColor(user.status),
                                                            border: '2px solid var(--bg-modal)',
                                                            display: 'inline-block',
                                                        }} />
                                                    </div>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <p style={{ color: 'var(--text-1)', fontWeight: 600, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {user.username || '—'}
                                                        </p>
                                                        <p style={{ color: 'var(--text-4)', fontSize: '0.6875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {user.role === 'admin' ? '👑 Admin · ' : ''}{getStatusLabel(user)}
                                                        </p>
                                                    </div>
                                                    {unread > 0 ? (
                                                        <span style={{
                                                            background: '#EF4444', color: '#fff',
                                                            fontSize: '0.625rem', fontWeight: 700,
                                                            borderRadius: '99px', minWidth: 20, height: 20,
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            padding: '0 4px', flexShrink: 0,
                                                            animation: 'glowPulse 2s infinite',
                                                        }}>
                                                            {unread > 99 ? '99+' : unread}
                                                        </span>
                                                    ) : (
                                                        <Send style={{ width: 14, height: 14, color: 'var(--text-4)', flexShrink: 0 }} />
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </>
                                )}
                            </div>
                        ) : (
                            <>
                                {/* Messages */}
                                <div
                                    ref={messagesContainerRef}
                                    onScroll={handleScroll}
                                    style={{ flex: 1, overflowY: 'auto', padding: '12px 12px 4px', display: 'flex', flexDirection: 'column', gap: 10 }}
                                >
                                    {displayMessages.length === 0 ? (
                                        <div style={{ textAlign: 'center', padding: '32px 0' }}>
                                            <MessageCircle style={{ width: 36, height: 36, color: 'var(--text-4)', margin: '0 auto 8px', opacity: 0.5 }} />
                                            <p style={{ color: 'var(--text-4)', fontSize: '0.875rem' }}>Aucun message</p>
                                            <p style={{ color: 'var(--text-4)', fontSize: '0.8125rem', marginTop: 4 }}>Soyez le premier à parler !</p>
                                        </div>
                                    ) : (
                                        displayMessages.map((msg, index) => {
                                            const isOwn = msg.user_id === currentUser.id;
                                            const isAdmin = currentUser.role === 'admin';
                                            const showDate = index === 0 || !isSameDay(displayMessages[index - 1].created_at, msg.created_at);

                                            return (
                                                <React.Fragment key={msg.id || index}>
                                                    {showDate && (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0' }}>
                                                            <div style={{ flex: 1, height: 1, background: 'var(--glass-border)' }} />
                                                            <span style={{ color: 'var(--text-4)', fontSize: '0.6875rem', whiteSpace: 'nowrap', padding: '0 4px' }}>{formatDateSep(msg.created_at)}</span>
                                                            <div style={{ flex: 1, height: 1, background: 'var(--glass-border)' }} />
                                                        </div>
                                                    )}
                                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flexDirection: isOwn ? 'row-reverse' : 'row' }}
                                                        className="group">
                                                        <div style={{
                                                            width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                                                            background: isOwn
                                                                ? 'linear-gradient(135deg, var(--accent), var(--accent-deep))'
                                                                : 'linear-gradient(135deg, #3B82F6, #06B6D4)',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            color: '#fff', fontSize: '0.8125rem', fontWeight: 700,
                                                        }}>
                                                            {(msg.username || '?').slice(0, 1).toUpperCase()}
                                                        </div>
                                                        <div style={{ flex: 1, minWidth: 0, textAlign: isOwn ? 'right' : 'left' }}>
                                                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexDirection: isOwn ? 'row-reverse' : 'row', marginBottom: 4 }}>
                                                                <span style={{ color: 'var(--text-1)', fontWeight: 700, fontSize: '0.8125rem' }}>{msg.username}</span>
                                                                <span style={{ color: 'var(--text-4)', fontSize: '0.6875rem' }}>{formatTime(msg.created_at)}</span>
                                                            </div>
                                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, flexDirection: isOwn ? 'row-reverse' : 'row' }}>
                                                                <p style={{
                                                                    display: 'inline-block',
                                                                    padding: '8px 12px',
                                                                    borderRadius: isOwn ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                                                                    background: isOwn ? 'linear-gradient(135deg, var(--accent), var(--accent-deep))' : 'var(--glass)',
                                                                    border: isOwn ? 'none' : '1px solid var(--glass-border)',
                                                                    color: isOwn ? '#fff' : 'var(--text-2)',
                                                                    fontSize: '0.875rem',
                                                                    lineHeight: 1.45,
                                                                    wordBreak: 'break-word',
                                                                    maxWidth: 220,
                                                                    textAlign: 'left',
                                                                }}>
                                                                    {msg.message}
                                                                </p>
                                                                {isAdmin && (
                                                                    <button
                                                                        onClick={() => deleteMessage(msg.id)}
                                                                        style={{
                                                                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                                            width: 24, height: 24, borderRadius: 'var(--r-sm)',
                                                                            background: 'transparent', border: 'none',
                                                                            color: '#EF4444', cursor: 'pointer',
                                                                            opacity: 0, transition: 'opacity 150ms ease',
                                                                            flexShrink: 0,
                                                                        }}
                                                                        className="group-hover:opacity-100"
                                                                    >
                                                                        <Trash2 style={{ width: 14, height: 14 }} />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </React.Fragment>
                                            );
                                        })
                                    )}
                                    <div ref={messagesEndRef} />
                                </div>

                                {isUserScrolling && (
                                    <div style={{ display: 'flex', justifyContent: 'center', padding: '6px 0', background: 'var(--glass)', borderTop: '1px solid var(--glass-border)', flexShrink: 0 }}>
                                        <button
                                            onClick={handleScrollToBottom}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: 6,
                                                padding: '5px 14px', borderRadius: 'var(--r-full)',
                                                background: 'var(--accent)', border: 'none', cursor: 'pointer',
                                                color: '#fff', fontSize: '0.8125rem', fontWeight: 600,
                                                boxShadow: '0 4px 12px var(--accent-glow)',
                                            }}
                                        >
                                            <ChevronDown style={{ width: 14, height: 14 }} />
                                            {newMessagesCount > 0
                                                ? `${newMessagesCount} nouveau${newMessagesCount > 1 ? 'x' : ''} message${newMessagesCount > 1 ? 's' : ''}`
                                                : 'Retour en bas'}
                                        </button>
                                    </div>
                                )}

                                {/* Input */}
                                <form onSubmit={sendMessage} style={{ padding: 12, background: 'var(--glass)', borderTop: '1px solid var(--glass-border)', flexShrink: 0 }}>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <input
                                            type="text"
                                            value={newMessage}
                                            onChange={(e) => setNewMessage(e.target.value)}
                                            placeholder={selectedUser ? `Message à ${selectedUser.username}...` : 'Votre message...'}
                                            maxLength={1000}
                                            style={{
                                                flex: 1,
                                                background: 'var(--bg-elevated)',
                                                border: '1px solid var(--glass-border)',
                                                borderRadius: 'var(--r-md)',
                                                padding: '8px 12px',
                                                color: 'var(--text-1)',
                                                fontSize: '0.875rem',
                                                outline: 'none',
                                                transition: 'border-color 200ms ease, box-shadow 200ms ease',
                                            }}
                                            onFocus={e => {
                                                e.currentTarget.style.borderColor = 'var(--accent)';
                                                e.currentTarget.style.boxShadow = '0 0 0 3px var(--accent-glow)';
                                            }}
                                            onBlur={e => {
                                                e.currentTarget.style.borderColor = 'var(--glass-border)';
                                                e.currentTarget.style.boxShadow = 'none';
                                            }}
                                            onKeyDown={(e) => e.stopPropagation()}
                                        />
                                        <button
                                            type="submit"
                                            disabled={!newMessage.trim() || sending}
                                            style={{
                                                width: 38, height: 38, borderRadius: 'var(--r-md)', flexShrink: 0,
                                                background: !newMessage.trim() || sending ? 'var(--glass)' : 'var(--accent)',
                                                border: '1px solid var(--glass-border)',
                                                color: !newMessage.trim() || sending ? 'var(--text-4)' : '#fff',
                                                cursor: !newMessage.trim() || sending ? 'not-allowed' : 'pointer',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                transition: 'background 200ms ease, color 200ms ease',
                                            }}
                                        >
                                            <Send style={{ width: 16, height: 16 }} />
                                        </button>
                                    </div>
                                    <p style={{ color: 'var(--text-4)', fontSize: '0.6875rem', marginTop: 5 }}>{newMessage.length}/1000 caractères</p>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default ChatWidget;
